import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Initializable } from 'src/core/common/domain/initializer/initializable';
import { StorableService } from 'src/core/storage/domain/storable-service';
import { Storage } from 'src/core/storage/domain/storage';
import { Nullable } from 'src/core/common/domain/types/types';
import { MOCKED_API_NETWORK_HOST, MOCKED_API_LOCAL_HOST } from 'src/environments/local';

export type Api = {
  id: string;
  isProduction: boolean;
  name: string;
  description: string;
  url: string;
  accessUrl: string;
  basicAuthorizationToken: string;
  usernameToken: string;
  passwordToken: string;
  usernameTokenHeader: string;
  passwordTokenHeader: string;
};

@Injectable({
  providedIn: 'root',
})
export class ApiService implements Initializable, StorableService {
  public static readonly KEY_CURRENT_API = 'api.current';
  private readySubject = new BehaviorSubject<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  ready$: Observable<boolean> = this.readySubject.asObservable();
  private current!: Api;

  // Please, ensure these hosts are configured in src/environments/local.ts
  private mockedLocalApiHost = `${MOCKED_API_LOCAL_HOST}`;
  private mockedNetworkApiHost = `${MOCKED_API_NETWORK_HOST}`;

  // NOTE: the first one is the default one
  private apis: Array<Api> = [
    {
      id: 'prod',
      isProduction: true,
      name: 'API Producción (por defecto)',
      description: 'RS no hace cambios aquí',
      url: 'https://api.iss.es/as_frontlineapp_global/v5',
      accessUrl: 'https://api.iss.es/token',
      basicAuthorizationToken: 'UGZzRXR1bEI5VG9NYndEaWVoM0dJZXZWbHdnYTo0V0N6UWFvb1N3S2RfN2o1ZkN0dWw0STlJZUFh',
      usernameToken: 'USR_FLA_PRO',
      passwordToken: 'ISSFLApro!',
      usernameTokenHeader: 'USR_FLA',
      passwordTokenHeader: 'ISSFLA!pro',
    },
    {
      id: 'pre',
      isProduction: false,
      name: 'API Pre-producción',
      description: 'Pruebas RS (e ISS)',
      url: 'https://api.iss.es/as_frontlineapp_global_pre/v5',
      accessUrl: 'https://api.iss.es/token/pre',
      basicAuthorizationToken: 'Wm01WkkwVE8yOVR6RTllVllPTWdFWGVSY0U4YTpSS0R4eVc3aUx5RGN3ZnZxNm9IbkxrSzlNeGNh',
      usernameToken: 'USR_FLA_PRE',
      passwordToken: 'ISSFLApre!',
      usernameTokenHeader: 'USR_FLA',
      passwordTokenHeader: 'ISSFLA!pre',
    },
    {
      id: 'dev',
      isProduction: false,
      name: 'API Desarrollo',
      description: 'Pruebas ISS',
      url: 'https://api.iss.es/as_frontlineapp_global_dev/v5',
      accessUrl: 'https://api.iss.es/token/dev',
      basicAuthorizationToken: 'bFk1YWVnTks5SzBNdVVSYkRvWklGZUxHWHE4YTo3SDFLMGdGS1hLUU9TaHdxdDhiQTdNdnhtR2Nh',
      usernameToken: 'USR_FLA_DEV',
      passwordToken: 'ISSFLAdev!',
      usernameTokenHeader: 'USR_FLA',
      passwordTokenHeader: 'ISSFLA!d',
    },
    {
      id: 'mocked-pre',
      isProduction: false,
      name: 'API Mocked PRE',
      description: 'Pruebas ISS',
      url: 'https://api.pre.ifa.rideosoftware.com',
      accessUrl: 'https://api.pre.ifa.rideosoftware.com/token',
      basicAuthorizationToken: 'bFk1YWVnTks5SzBNdVVSYkRvWklGZUxHWHE4YTo3SDFLMGdGS1hLUU9TaHdxdDhiQTdNdnhtR2Nh',
      usernameToken: 'USR_FLA_DEV',
      passwordToken: 'ISSFLAdev!',
      usernameTokenHeader: 'USR_FLA',
      passwordTokenHeader: 'ISSFLA!d',
    },
    {
      id: 'mocked-qa',
      isProduction: false,
      name: 'API Mocked QA',
      description: 'Pruebas RS',
      url: 'https://api.qa.ifa.rideosoftware.com',
      accessUrl: 'https://api.qa.ifa.rideosoftware.com/token',
      basicAuthorizationToken: 'bFk1YWVnTks5SzBNdVVSYkRvWklGZUxHWHE4YTo3SDFLMGdGS1hLUU9TaHdxdDhiQTdNdnhtR2Nh',
      usernameToken: 'USR_FLA_DEV',
      passwordToken: 'ISSFLAdev!',
      usernameTokenHeader: 'USR_FLA',
      passwordTokenHeader: 'ISSFLA!d',
    },
    {
      id: 'mocked-develop',
      isProduction: false,
      name: 'API Mocked Develop',
      description: 'Pruebas RS',
      url: 'https://api.develop.ifa.rideosoftware.com',
      accessUrl: 'https://api.develop.ifa.rideosoftware.com/token',
      basicAuthorizationToken: 'bFk1YWVnTks5SzBNdVVSYkRvWklGZUxHWHE4YTo3SDFLMGdGS1hLUU9TaHdxdDhiQTdNdnhtR2Nh',
      usernameToken: 'USR_FLA_DEV',
      passwordToken: 'ISSFLAdev!',
      usernameTokenHeader: 'USR_FLA',
      passwordTokenHeader: 'ISSFLA!d',
    },
    {
      id: 'mocked-local',
      isProduction: false,
      name: 'API Mocked Local',
      description: 'Máquina local',
      url: `${this.mockedLocalApiHost}`,
      accessUrl: `${this.mockedLocalApiHost}/token`,
      basicAuthorizationToken: 'bFk1YWVnTks5SzBNdVVSYkRvWklGZUxHWHE4YTo3SDFLMGdGS1hLUU9TaHdxdDhiQTdNdnhtR2Nh',
      // RS_SETTING Enable when DIST. Enable when using Mocked API 2.0
      usernameToken: 'dev_username',
      passwordToken: 'dev_password',
      usernameTokenHeader: 'dev_username',
      passwordTokenHeader: 'dev_password',
      // RS_END_SETTING
      // RS_SETTING Comment when DIST. Enable when using Mocked API 1.0
      // usernameToken: 'USR_FLA_DEV',
      // passwordToken: 'ISSFLAdev!',
      // usernameTokenHeader: 'USR_FLA',
      // passwordTokenHeader: 'ISSFLA!d',
      //RS_END_SETTING
    },
    {
      id: 'mocked-network',
      isProduction: false,
      name: 'API Mocked Red',
      description: 'Máquina local',
      url: `${this.mockedNetworkApiHost}`,
      accessUrl: `${this.mockedNetworkApiHost}/token`,
      basicAuthorizationToken: 'bFk1YWVnTks5SzBNdVVSYkRvWklGZUxHWHE4YTo3SDFLMGdGS1hLUU9TaHdxdDhiQTdNdnhtR2Nh',
      // RS_SETTING Enable when DIST. Enable when using Mocked API 2.0
      usernameToken: 'dev_username',
      passwordToken: 'dev_password',
      usernameTokenHeader: 'dev_username',
      passwordTokenHeader: 'dev_password',
      // RS_END_SETTING
      // RS_SETTING Comment when DIST. Enable when using Mocked API 1.0
      // usernameToken: 'USR_FLA_DEV',
      // passwordToken: 'ISSFLAdev!',
      // usernameTokenHeader: 'USR_FLA',
      // passwordTokenHeader: 'ISSFLA!d',
      // RS_END_SETTING
    },
  ];

  constructor(private storage: Storage) {}

  async init(): Promise<void> {
    await this.initFromStorage();

    this.readySubject.next(true);
  }

  async clear(): Promise<void> {
    // we don't remove anything because it has to have a target API
  }

  getAvailableApis(): Array<Api> {
    return this.apis;
  }

  currentApi(): Api {
    return this.current;
  }

  baseUrl(): string {
    return this.currentApi().url;
  }

  async selectApi(id: string) {
    this.current = this.findById(id);
    await this.save();
  }

  getUsernameToken(): Nullable<string> {
    return this.currentApi().usernameToken;
  }

  getPasswordToken(): Nullable<string> {
    return this.currentApi().passwordToken;
  }

  getUsernameTokenHeader(): Nullable<string> {
    return this.currentApi().usernameTokenHeader;
  }

  getPasswordTokenHeader(): Nullable<string> {
    return this.currentApi().passwordTokenHeader;
  }

  getBasicAuthorizationToken(): string {
    return this.currentApi().basicAuthorizationToken;
  }

  baseAccessUrl(): string {
    return this.currentApi().accessUrl;
  }

  isProduction(): boolean {
    if (!this.currentApi()) {
      return true;
    }

    return this.currentApi().isProduction;
  }

  private async initFromStorage(): Promise<void> {
    let apiId = (await this.storage.get(ApiService.KEY_CURRENT_API)) ?? null;
    if (apiId === 'mocked-cloud') {
      apiId = 'mocked-network';
      await this.storage.set(ApiService.KEY_CURRENT_API, apiId);
    }

    if (apiId === null) {
      this.current = this.getDefaultApi();
      await this.save();
    } else {
      this.current = this.findById(apiId);
    }
  }

  private findById(id: string): Api {
    return this.apis.find((api: Api) => api.id === id);
  }

  private async save() {
    await this.storage.set(ApiService.KEY_CURRENT_API, this.current.id);
    await this.initFromStorage();
  }

  private getDefaultApi(): Api {
    return this.apis[0];
  }
}
